/* This example requires Tailwind CSS v2.0+ */
import React, { Fragment } from 'react';
import { Listbox as HeadlessListBox, Transition } from '@headlessui/react';
import s from './ListBox.module.css';
import cs from 'classnames';

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ');
}
export function ListBox<T>({
    children,
    onChange,
    value,
    disabled,
}: {
    value: T;
    onChange: (val: T) => void;
    disabled: boolean;
    children: React.FC<{ open: boolean }>;
}) {
    return (
        <HeadlessListBox value={value} onChange={onChange} disabled={disabled}>
            {children}
        </HeadlessListBox>
    );
}

function ListBoxBody({
    disabled = false,
    children,
    hideMargin = false,
}: {
    disabled?: boolean;
    children: React.ReactNode;
    hideMargin?: boolean;
}) {
    return (
        <div
            className={`group ${hideMargin ? '' : 'mt-1'} relative w-full`}
            aria-disabled={disabled}>
            {children}
        </div>
    );
}

function ListBoxOptions({
    open,
    children,
    top = false,
}: {
    open: boolean;
    children: React.ReactNode;
    top?: boolean;
}) {
    const classNames = cs(
        'absolute z-1000 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm',
        {
            ['-mt-20']: top,
            ['mt-1']: !top,
        },
    );
    return (
        <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <HeadlessListBox.Options static className={classNames}>
                {children}
            </HeadlessListBox.Options>
        </Transition>
    );
}

function ListBoxOption({
    disabled = false,
    id,
    value,
    children,
}: {
    disabled?: boolean;
    id: string | number;
    value: any;
    children: React.FC<{ active: boolean; selected: any; disabled: boolean }>;
}) {
    return (
        <HeadlessListBox.Option
            key={id}
            disabled={disabled}
            className={({ active, selected }) =>
                classNames(
                    active && !selected
                        ? 'bg-teal-100 cursor-pointer'
                        : 'text-passport cursor-pointer',
                    selected ? 'bg-teal-100' : '',
                    'cursor-default relative py-2 pl-4 pr-4',
                    disabled ? 'text-opacity-40' : '',
                )
            }
            value={value}>
            {({ active, selected, disabled }) => children({ active, selected, disabled })}
        </HeadlessListBox.Option>
    );
}

function ListBoxLabel({
    children,
    className = '',
}: {
    children: React.ReactNode;
    className?: string;
}) {
    const rootLabelClass = classNames(s.rootLabelBase, className ? className : s.rootLabelDefault);
    return <HeadlessListBox.Label className={rootLabelClass}>{children}</HeadlessListBox.Label>;
}

function ListBoxButton({
    children,
    disabled,
    className = '',
    size = 'large',
    hasError,
}: {
    children: React.ReactNode;
    disabled?: boolean;
    className?: string;
    size?: 'large' | 'small' | 'xsmall';
    hasError?: boolean;
}) {
    const rootListbox = cs(
        s.rootListboxBase,
        {
            [s.rootListboxDefault]: !hasError,
            [s.rootListboxError]: hasError,
            [s.large]: size === 'large',
            [s.small]: size === 'small',
            [s.xsmall]: size === 'xsmall',
        },
        className,
        disabled ? 'disabled:bg-opacity-40 disabled:text-gray-400 disabled:border-opacity-40' : '',
    );

    return (
        <HeadlessListBox.Button disabled={disabled} className={rootListbox}>
            {children}
        </HeadlessListBox.Button>
    );
}

ListBox.Body = ListBoxBody;
ListBox.Label = ListBoxLabel;
ListBox.Button = ListBoxButton;
ListBox.Options = ListBoxOptions;
ListBox.Option = ListBoxOption;
